import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { yaml } from '@codemirror/lang-yaml';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import type { EditorView as EditorViewType } from '@codemirror/view';
import { DraftYamlEditorPane } from './guideline/DraftYamlEditorPane';
import {
  buildYamlDiffLineMappings,
  buildYamlSideBySideSectionFromText,
  collectDiffChangeLocations,
} from '../services/guidelineSideBySideDiff';
import {
  refreshDiffDecorations,
  yamlDiffDecorationsFromRefs,
} from '../services/guidelineYamlDiffDecorations';
import { draftContentToYaml, parseDraftContentYaml } from '../services/guidelineDraftYaml';
import type { GuidelineDraftContent } from '../types/accuracy';
import {
  ChangeNavigator,
  DiffOverviewRuler,
  type OverviewMarker,
} from './GuidelineDiffOverview';
import './GuidelineSideBySideCompare.css';

const PARENT_SYNC_DELAY_MS = 400;

function yamlDiagnostics(view: { state: { doc: { toString: () => string } } }) {
  try {
    parseDraftContentYaml(view.state.doc.toString());
    return [];
  } catch (e) {
    return [
      {
        from: 0,
        to: 1,
        severity: 'error' as const,
        message: e instanceof Error ? e.message : 'YAML エラー',
      },
    ];
  }
}

function scrollEditorToLine(view: EditorViewType, lineNo: number | null) {
  if (lineNo == null || lineNo < 1) return;
  const clamped = Math.min(Math.max(lineNo, 1), view.state.doc.lines);
  const line = view.state.doc.line(clamped);
  view.dispatch({
    effects: EditorView.scrollIntoView(line.from, { y: 'center' }),
    selection: { anchor: line.from },
  });
  view.focus();
}

export function GuidelineYamlCompareEditor({
  original,
  yamlText,
  onYamlTextChange,
  registerGetDraftYaml,
  onChange,
  originalTitle,
  draftTitle,
  readOnly = false,
}: {
  original: GuidelineDraftContent;
  yamlText: string;
  onYamlTextChange: (text: string) => void;
  registerGetDraftYaml?: (getText: (() => string) | null) => void;
  onChange: (content: GuidelineDraftContent) => void;
  originalTitle: string;
  draftTitle: string;
  readOnly?: boolean;
}) {
  const originalYaml = useMemo(() => draftContentToYaml(original), [original]);

  const draftYamlRef = useRef(yamlText);
  const [draftYaml, setDraftYaml] = useState(yamlText);
  const [draftEditorKey, setDraftEditorKey] = useState(0);
  const [draftEditorInitialYaml, setDraftEditorInitialYaml] = useState(yamlText);

  const parentSyncTimerRef = useRef<number | null>(null);
  const skipYamlTextPropSyncRef = useRef(false);

  const [activeChangeIndex, setActiveChangeIndex] = useState(0);
  const [markers, setMarkers] = useState<OverviewMarker[]>([]);
  const [viewport, setViewport] = useState({ topPct: 0, heightPct: 100 });

  const leftViewRef = useRef<EditorViewType | null>(null);
  const rightViewRef = useRef<EditorViewType | null>(null);
  const scrollRef = useRef<HTMLElement | null>(null);
  const syncingScrollRef = useRef(false);
  const [editorsReady, setEditorsReady] = useState(0);

  const reloadDraftEditor = useCallback(
    (text: string) => {
      draftYamlRef.current = text;
      setDraftYaml(text);
      setDraftEditorInitialYaml(text);
      setDraftEditorKey((k) => k + 1);
    },
    []
  );

  useEffect(() => {
    if (!registerGetDraftYaml) return;
    registerGetDraftYaml(() => draftYamlRef.current);
    return () => registerGetDraftYaml(null);
  }, [registerGetDraftYaml]);

  // Sync when parent yamlText changes (e.g. YAML tab → compare), not from our own edits.
  useEffect(() => {
    if (skipYamlTextPropSyncRef.current) {
      skipYamlTextPropSyncRef.current = false;
      return;
    }
    if (yamlText === draftYamlRef.current) return;
    reloadDraftEditor(yamlText);
  }, [yamlText, reloadDraftEditor]);

  const flushDraftToParent = useCallback(
    (text: string) => {
      try {
        onChange(parseDraftContentYaml(text));
      } catch {
        // Parent keeps last valid parsed draft while YAML is temporarily invalid.
      }
    },
    [onChange]
  );

  const scheduleParentSync = useCallback(
    (text: string) => {
      if (parentSyncTimerRef.current != null) {
        window.clearTimeout(parentSyncTimerRef.current);
      }
      parentSyncTimerRef.current = window.setTimeout(() => {
        parentSyncTimerRef.current = null;
        flushDraftToParent(text);
      }, PARENT_SYNC_DELAY_MS);
    },
    [flushDraftToParent]
  );

  useEffect(
    () => () => {
      if (readOnly) return;
      if (parentSyncTimerRef.current != null) {
        window.clearTimeout(parentSyncTimerRef.current);
      }
      flushDraftToParent(draftYamlRef.current);
    },
    [flushDraftToParent, readOnly]
  );

  const diffSection = useMemo(
    () => buildYamlSideBySideSectionFromText(originalYaml, draftYaml),
    [originalYaml, draftYaml]
  );

  const lineMappings = useMemo(
    () => (diffSection ? buildYamlDiffLineMappings(diffSection.rows) : []),
    [diffSection]
  );

  const lineMappingsRef = useRef(lineMappings);
  lineMappingsRef.current = lineMappings;
  const activeDiffRowIndexRef = useRef<number | null>(null);

  const sections = useMemo(() => (diffSection ? [diffSection] : []), [diffSection]);
  const changeLocations = useMemo(() => collectDiffChangeLocations(sections), [sections]);

  const changeKeyToIndex = useMemo(() => {
    const map = new Map<string, number>();
    changeLocations.forEach((loc, index) => map.set(loc.key, index));
    return map;
  }, [changeLocations]);

  const activeDiffRowIndex = useMemo(() => {
    const loc = changeLocations[activeChangeIndex];
    return loc?.rowIndex ?? null;
  }, [changeLocations, activeChangeIndex]);

  activeDiffRowIndexRef.current = activeDiffRowIndex;

  useEffect(() => {
    refreshDiffDecorations(leftViewRef.current);
    refreshDiffDecorations(rightViewRef.current);
  }, [lineMappings, activeDiffRowIndex]);

  const draftExtensions = useMemo(
    () => [
      yaml(),
      yamlDiffDecorationsFromRefs('right', lineMappingsRef, activeDiffRowIndexRef),
      linter(yamlDiagnostics),
      lintGutter(),
    ],
    []
  );

  const readOnlyExtensions = useMemo(
    () => [
      yaml(),
      yamlDiffDecorationsFromRefs('left', lineMappingsRef, activeDiffRowIndexRef),
      EditorView.editable.of(false),
    ],
    []
  );

  const syncScroll = useCallback((source: EditorViewType, target: EditorViewType) => {
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    target.scrollDOM.scrollTop = source.scrollDOM.scrollTop;
    target.scrollDOM.scrollLeft = source.scrollDOM.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }, []);

  const scrollBothToRatio = useCallback((ratio: number) => {
    const views = [leftViewRef.current, rightViewRef.current].filter(Boolean) as EditorViewType[];
    for (const view of views) {
      const el = view.scrollDOM;
      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = ratio * maxScroll;
    }
  }, []);

  const recomputeOverview = useCallback(() => {
    const scrollEl = scrollRef.current;
    const view = rightViewRef.current;
    if (!scrollEl || !view || !diffSection) return;

    const scrollHeight = scrollEl.scrollHeight;
    const clientHeight = scrollEl.clientHeight;
    if (scrollHeight <= 0) return;

    const nextMarkers: OverviewMarker[] = [];
    diffSection.rows.forEach((row, rowIndex) => {
      const hasRemove = row.left.kind === 'remove';
      const hasAdd = row.right.kind === 'add';
      if (!hasRemove && !hasAdd) return;

      const mapping = lineMappings[rowIndex];
      const lineNo = mapping?.rightLine;
      if (!lineNo || lineNo > view.state.doc.lines) return;

      const line = view.state.doc.line(lineNo);
      const block = view.lineBlockAt(line.from);

      nextMarkers.push({
        changeKey: `${diffSection.id}:${rowIndex}`,
        changeIndex: changeKeyToIndex.get(`${diffSection.id}:${rowIndex}`),
        hasRemove,
        hasAdd,
        topPct: (block.top / scrollHeight) * 100,
        heightPct: Math.max((block.height / scrollHeight) * 100, 0.35),
      });
    });

    setMarkers(nextMarkers);
    setViewport({
      topPct: (scrollEl.scrollTop / scrollHeight) * 100,
      heightPct: Math.min((clientHeight / scrollHeight) * 100, 100),
    });
  }, [diffSection, lineMappings, changeKeyToIndex]);

  useEffect(() => {
    const left = leftViewRef.current;
    const right = rightViewRef.current;
    if (!left || !right) return;

    scrollRef.current = right.scrollDOM;

    const onLeftScroll = () => {
      syncScroll(left, right);
      recomputeOverview();
    };
    const onRightScroll = () => {
      syncScroll(right, left);
      recomputeOverview();
    };

    left.scrollDOM.addEventListener('scroll', onLeftScroll, { passive: true });
    right.scrollDOM.addEventListener('scroll', onRightScroll, { passive: true });
    recomputeOverview();

    return () => {
      left.scrollDOM.removeEventListener('scroll', onLeftScroll);
      right.scrollDOM.removeEventListener('scroll', onRightScroll);
    };
  }, [editorsReady, syncScroll, recomputeOverview]);

  useEffect(() => {
    recomputeOverview();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => recomputeOverview());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeOverview, draftYaml, originalYaml]);

  const scrollToChange = useCallback(
    (index: number) => {
      const loc = changeLocations[index];
      const mapping = loc ? lineMappings[loc.rowIndex] : undefined;
      const view = rightViewRef.current;
      const leftView = leftViewRef.current;
      if (!loc || !mapping || !view) return;
      setActiveChangeIndex(index);
      scrollEditorToLine(view, mapping.rightLine);
      if (leftView) scrollEditorToLine(leftView, mapping.leftLine);
      recomputeOverview();
    },
    [changeLocations, lineMappings, recomputeOverview]
  );

  useEffect(() => {
    if (changeLocations.length === 0) return;
    setActiveChangeIndex(0);
    const timer = window.setTimeout(() => scrollToChange(0), 50);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- jump only when comparison target changes
  }, [originalYaml]);

  const handleDraftYamlChange = (value: string) => {
    if (readOnly) return;
    draftYamlRef.current = value;
    setDraftYaml(value);
    skipYamlTextPropSyncRef.current = true;
    onYamlTextChange(value);
    scheduleParentSync(value);
  };

  return (
    <div className="glsbc-root glsbc-yaml-compare-root">
      <div className="glsbc-legend">
        <span className="glsbc-legend-remove">左：− 比較対象から削除された行（赤）</span>
        <span className="glsbc-legend-add">右：+ ドラフトに追加された行（緑）</span>
        <span className="glsbc-legend-yaml">YAML 折りたたみ・行単位比較に対応</span>
      </div>

      {changeLocations.length > 0 && (
        <ChangeNavigator
          locations={changeLocations}
          activeIndex={activeChangeIndex}
          onSelect={scrollToChange}
        />
      )}

      {diffSection && (
        <div className="glsbc-scroll-shell glsbc-cm-compare-shell">
          <div className="glsbc-cm-pair">
            <div className="glsbc-compare-column">
              <div className="glsbc-column-header glsbc-column-header-left">{originalTitle}</div>
              <div className="glsbc-cm-pane glsbc-cm-pane-readonly">
                <CodeMirror
                  key={originalYaml}
                  value={originalYaml}
                  height="100%"
                  editable={false}
                  basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
                  extensions={readOnlyExtensions}
                  onCreateEditor={(view) => {
                    leftViewRef.current = view;
                    setEditorsReady((n) => n + 1);
                  }}
                />
              </div>
            </div>
            <div className="glsbc-compare-column">
              <div className="glsbc-column-header glsbc-column-header-right">{draftTitle}</div>
              <div className="glsbc-cm-pane glsbc-cm-pane-draft">
                <DraftYamlEditorPane
                  editorKey={draftEditorKey}
                  initialText={draftEditorInitialYaml}
                  height="100%"
                  extensions={draftExtensions}
                  readOnly={readOnly}
                  onChange={handleDraftYamlChange}
                  onCreateEditor={(view) => {
                    rightViewRef.current = view;
                    scrollRef.current = view.scrollDOM;
                    setEditorsReady((n) => n + 1);
                  }}
                />
              </div>
            </div>
          </div>
          <DiffOverviewRuler
            scrollRef={scrollRef}
            markers={markers}
            viewportTopPct={viewport.topPct}
            viewportHeightPct={viewport.heightPct}
            onSelectChange={scrollToChange}
            onScrollSync={recomputeOverview}
            onScrollToRatio={scrollBothToRatio}
          />
        </div>
      )}
    </div>
  );
}
